import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type { Company, DarfPlan, RevenueCode } from "../types";

type Flash = { kind: "ok" | "error"; text: string } | null;
type Props = { companies: Company[]; flash: (value: Flash) => void };
type Draft = {
  company_id: number; competence: string; name: string; due_date: string;
  due_date_source: string; lead_days: number; revenue_codes: string[]; enabled: boolean;
};

const previousMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
const initialMonth = `${previousMonth.getFullYear()}-${String(previousMonth.getMonth() + 1).padStart(2, "0")}`;
const blank = (companyId: number, competence: string): Draft => ({
  company_id: companyId,
  competence,
  name: "",
  due_date: "",
  due_date_source: "",
  lead_days: 0,
  revenue_codes: [],
  enabled: true,
});
const displayDate = (value: string | null) => {
  if (!value) return "Aguardando vencimento";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

export default function DarfPlansPage({ companies, flash }: Props) {
  const [companyId, setCompanyId] = useState(companies[0]?.id ?? 0);
  const [month, setMonth] = useState(initialMonth);
  const competence = month ? `${month.slice(5, 7)}/${month.slice(0, 4)}` : "";
  const [catalog, setCatalog] = useState<RevenueCode[]>([]);
  const [plans, setPlans] = useState<DarfPlan[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [origin, setOrigin] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<{ items: RevenueCode[] }>("/api/darf-plans/catalog")
      .then((result) => setCatalog(result.items))
      .catch((error) => flash({ kind: "error", text: error.message }));
  }, []);
  useEffect(() => {
    if (!companyId || !competence) return;
    api<{ items: DarfPlan[] }>(`/api/darf-plans?company_id=${companyId}&competence=${encodeURIComponent(competence)}`)
      .then((result) => setPlans(result.items))
      .catch((error) => flash({ kind: "error", text: error.message }));
  }, [companyId, competence]);

  const eligible = useMemo(() => catalog.filter((item) => item.document_type.toUpperCase() !== "DAE"), [catalog]);
  const origins = useMemo(() => [...new Set(eligible.map((item) => item.origin))], [eligible]);
  const matches = useMemo(() => eligible.filter((item) => {
    const text = `${item.code} ${item.description}`.toLowerCase();
    return (!origin || item.origin === origin) && (!query || text.includes(query.toLowerCase()));
  }).slice(0, 80), [eligible, origin, query]);

  function startNew() {
    setEditingId(null);
    setDraft(blank(companyId, competence));
    setQuery("");
  }
  function edit(plan: DarfPlan) {
    setEditingId(plan.id);
    setDraft({
      company_id: plan.company_id,
      competence: plan.competence || competence,
      name: plan.name,
      due_date: plan.due_date || "",
      due_date_source: plan.due_date_source || "",
      lead_days: plan.lead_days,
      revenue_codes: plan.revenue_codes,
      enabled: plan.enabled,
    });
  }
  function toggleCode(code: string) {
    if (!draft) return;
    const selected = new Set(draft.revenue_codes);
    if (selected.has(code)) selected.delete(code);
    else selected.add(code);
    setDraft({ ...draft, revenue_codes: [...selected].sort() });
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!draft?.revenue_codes.length) {
      flash({ kind: "error", text: "Selecione ao menos um código de receita." });
      return;
    }
    setSaving(true);
    try {
      await api(editingId ? `/api/darf-plans/${editingId}` : "/api/darf-plans", {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify(draft),
      });
      const response = await api<{ items: DarfPlan[] }>(`/api/darf-plans?company_id=${companyId}&competence=${encodeURIComponent(competence)}`);
      setPlans(response.items);
      setDraft(null);
      flash({ kind: "ok", text: "Grupo e vencimento registrados para revisão." });
    } catch (error) {
      flash({ kind: "error", text: error instanceof Error ? error.message : "Falha ao salvar grupo" });
    } finally {
      setSaving(false);
    }
  }

  return <>
    <div className="page-head">
      <div>
        <p className="eyebrow">PROGRAMAÇÃO</p>
        <h1>Exceções por empresa</h1>
        <p>Veja os grupos padrão vinculados e cadastre exceções exclusivas da empresa.</p>
      </div>
      <button className="primary" onClick={startNew} disabled={!companyId || !competence}>+ Grupo exclusivo</button>
    </div>
    <div className="notice">Os grupos ficam em preparação. A busca por códigos será ativada após validar a seleção de débitos na DCTFWeb e a conferência do PDF.</div>
    <section className="card darf-filters">
      <label>Empresa
        <select value={companyId} onChange={(event) => { setCompanyId(Number(event.target.value)); setDraft(null); }}>
          {companies.map((company) => <option key={company.id} value={company.id}>{company.trade_name || company.legal_name}</option>)}
        </select>
      </label>
      <label>Competência
        <input type="month" value={month} onChange={(event) => { setMonth(event.target.value); setDraft(null); }} />
      </label>
    </section>
    <section className="card table-card">
      {plans.length ? <table><thead><tr><th>Grupo</th><th>Vencimento efetivo</th><th>Buscar a partir de</th><th>Códigos</th><th>Situação</th><th /></tr></thead>
        <tbody>{plans.map((plan) => <tr key={plan.id}>
          <td><strong>{plan.name}</strong><small>{plan.scope === "standard" ? "Grupo padrão" : "Exclusivo desta empresa"}{plan.due_date_source ? <> · <a href={plan.due_date_source} target="_blank" rel="noreferrer">Ver fonte ↗</a></> : " · Fonte ainda não informada"}</small></td>
          <td>{displayDate(plan.due_date)}</td>
          <td>{displayDate(plan.search_from)}<small>{plan.lead_days} dia(s) corridos antes</small></td>
          <td>{plan.revenue_codes.length}<small>{plan.scope === "exclusive" ? "Ver em Editar" : "Ver em Regras"}</small></td>
          <td><span className="pill inactive">{plan.enabled ? "Em preparação" : "Desativado"}</span></td>
          <td>{plan.scope === "exclusive" ? <button className="table-action" onClick={() => edit(plan)}>Editar</button> : <small>Editar em Grupos padrão</small>}</td>
        </tr>)}</tbody></table> : <div className="empty"><p>Nenhum grupo configurado para esta empresa e competência.</p></div>}
    </section>
    {draft && <section className="card darf-editor">
      <h2>{editingId ? "Editar grupo" : "Novo grupo"}</h2>
      <form onSubmit={save} className="form-grid">
        <label>Nome do grupo
          <input required minLength={2} maxLength={120} placeholder="Ex.: eSocial — dia 20" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </label>
        <label>Vencimento efetivo
          <input required type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} />
        </label>
        <label>Buscar quantos dias corridos antes
          <input required type="number" min="0" max="31" value={draft.lead_days} onChange={(e) => setDraft({ ...draft, lead_days: Number(e.target.value) })} />
        </label>
        <label>Fonte do vencimento
          <input required type="url" placeholder="Agenda Tributária ou Sicalc" value={draft.due_date_source} onChange={(e) => setDraft({ ...draft, due_date_source: e.target.value })} />
        </label>
        <label className="check-label wide"><input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> Grupo habilitado para futura automação</label>
        <div className="form-section wide"><h3>Códigos do DARF ({draft.revenue_codes.length} selecionados)</h3><p>Selecione os códigos exatos, inclusive o sufixo. Códigos exclusivos de DAE não aparecem.</p></div>
        <label>Origem
          <select value={origin} onChange={(e) => setOrigin(e.target.value)}><option value="">Todas</option>{origins.map((item) => <option key={item}>{item}</option>)}</select>
        </label>
        <label>Buscar código ou descrição
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ex.: 1082" />
        </label>
        <div className="wide darf-codes">{matches.map((item) => <label key={item.code} className="darf-code">
          <input type="checkbox" checked={draft.revenue_codes.includes(item.code)} onChange={() => toggleCode(item.code)} />
          <span><strong>{item.code}</strong> · {item.origin}<small>{item.description}</small></span>
        </label>)}</div>
        {draft.revenue_codes.length > 0 && <p className="wide darf-selected">Selecionados: {draft.revenue_codes.join(", ")}</p>}
        <footer className="wide"><button type="button" className="secondary" onClick={() => setDraft(null)}>Cancelar</button><button className="primary" disabled={saving}>{saving ? "Salvando…" : "Salvar grupo"}</button></footer>
      </form>
    </section>}
  </>;
}
